<template>
  <div class="dashboard-view">
    <ftu-block
      dismiss-key="dashboard"
      icon="space_dashboard"
      title="This is your Dashboard"
      description="The Dashboard displays a variety of high-level information about the sports card market. The graph displays the total sales volume tracked by Card Ladder per day. Below that, you'll find various helpful widgets to help you in your collecting knowledge, such as deals ending soon."
    />

    <page-header title="Dashboard">
      <template #actions>
        <icon-button
          icon="settings"
          @click="customizeModalOpen = true"
        />
      </template>
    </page-header>

    <div v-if="loaded" class="dashboard-content">
      <component
        :is="component"
        v-for="({ component }, i) in dashboardWidgets"
        :key="component"
        :data="dashboard"
        :zoom.sync="zoom"
        :scale.sync="scale"
        :bottom="i === dashboardWidgets.length - 1"
      />
    </div>

    <spinner v-else />

    <welcome-modal />

    <modal
      title="Customize Your Dashboard"
      :open="customizeModalOpen"
      @close="cancelConfigChanges"
    >
      <draggable
        v-model="widgetOptions"
        handle=".handle"
        ghost-class="ghost"
        class="draggable-container"
      >
        <align
          v-for="widget in widgetOptions"
          :key="widget.component"
          flex
          :class="{ disabled: !widget.visible }"
        >
          <align small-gap>
            <icon class="handle" name="drag_handle" />

            <span>{{ DASHBOARD_WIDGETS[widget.component].name }}</span>
          </align>

          <icon-button
            :icon="widget.visible ? 'visibility' : 'visibility_off'"
            @click="widget.visible = !widget.visible"
          />
        </align>
      </draggable>

      <div class="button-row">
        <btn @click="saveConfigChanges">
          Save
        </btn>

        <btn kind="secondary" @click="cancelConfigChanges">
          Cancel
        </btn>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';
import draggable from 'vuedraggable';
import clone from 'lodash.clonedeep';

import { DASHBOARD_WIDGETS } from '@cardladder/shared/constants';
import { authService, indexService } from '@/services';

import WelcomeModal from '@/auth/WelcomeModal';
import ShopWidget from './widgets/ShopWidget';
import CollectionWidget from './widgets/CollectionWidget';
import IndexWidget from './widgets/IndexWidget';
import HeadlinesWidget from './widgets/HeadlinesWidget';
import SalesRecapWidget from './widgets/SalesRecapWidget';
import SalesVolumeWidget from './widgets/SalesVolumeWidget';

export default {
  components: {
    draggable,
    WelcomeModal,
    ShopWidget,
    CollectionWidget,
    IndexWidget,
    HeadlinesWidget,
    SalesRecapWidget,
    SalesVolumeWidget,
  },

  data() {
    return {
      dashboard: {},
      loaded: false,
      zoom: 730,
      scale: 'linear',
      customizeModalOpen: false,
      DASHBOARD_WIDGETS,
      widgetOptions: [],
    };
  },

  created() {
    this.load();
  },

  mounted() {
    this.widgetOptions = clone([...this.userWidgets]);
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    userWidgets() {
      if (!this.user || !this.user.dashboardWidgets) {
        return [];
      }

      return this.user.dashboardWidgets.filter(({ component }) => Boolean(DASHBOARD_WIDGETS[component]));
    },

    dashboardWidgets() {
      return this.userWidgets.filter(({ visible }) => visible);
    },
  },

  methods: {
    async load() {
      this.loaded = false;

      try {
        this.dashboard = await indexService.getDashboard();
      } catch (e) {
        this.$toasted.error('Error loading dashboard');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    async saveConfigChanges() {
      this.customizeModalOpen = false;

      try {
        await authService.updateUser(this.user.uid, {
          dashboardWidgets: this.widgetOptions,
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    },

    cancelConfigChanges() {
      this.widgetOptions = clone([...this.userWidgets]);
      this.customizeModalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-view {
  @include container;
  min-height: 100%;
}

.dashboard-content > *:not(:last-child) {
  margin-bottom: $gp * 2;
}

.handle {
  cursor: grab;
}

.ghost {
  background-color: var(--theme-highlight);
}

.draggable-container {
  margin-bottom: $gp;

  > * {
    padding: $gp * .5;
    border: solid 1px var(--theme-border);
    border-radius: $border-radius;
    margin-bottom: $gp * .5;

    &.disabled {
      --theme-icon-button-color: #{$color-gray-5};
      color: $color-gray-5;
    }
  }
}
</style>
